import produce from 'immer';

import {ChartAggOption} from '../../generated/graphql';
import {legendTemplateRemoveCrosshairValues} from '../../util/legend';
import {type ChartAreaOption} from './chartTypes';
import {getLegendOverrideKeyBackwardsCompatible} from './legend';
import {type Bar, type Line, type Mark} from './types';

export function isChartAreaOption(s: string): s is ChartAreaOption {
  return (
    s === 'minmax' ||
    s === 'stddev' ||
    s === 'stderr' ||
    s === 'none' ||
    s === 'samples'
  );
}

export function isChartAggOption(s: string): s is ChartAggOption {
  return (
    s === ChartAggOption.Mean ||
    s === ChartAggOption.Max ||
    s === ChartAggOption.Median ||
    s === ChartAggOption.Min ||
    s === ChartAggOption.Sum ||
    s === ChartAggOption.Samples
  );
}

export function overrideLineTitles(
  lines: Line[],
  overrides: {[key: string]: string},
  useRunOrGroupId: boolean
): Line[] {
  let result: Line[] | undefined;

  lines.forEach((line, index) => {
    const lineKey = getLegendOverrideKeyBackwardsCompatible(
      line,
      useRunOrGroupId,
      overrides
    );
    const hasOverride = lineKey in overrides;
    const overrideTitle = hasOverride ? overrides[lineKey] : undefined;
    const title = hasOverride ? overrideTitle : line.title;
    const legend =
      hasOverride && line.fancyTitle
        ? legendTemplateRemoveCrosshairValues(overrideTitle!)
        : undefined;
    const originalTitleChanged =
      !Object.hasOwn(line, 'originalTitle') ||
      line.originalTitle !== line.title;
    const titleChanged = hasOverride && line.title !== title;
    const legendChanged = legend != null && line.fancyTitle?.legend !== legend;

    if (!originalTitleChanged && !titleChanged && !legendChanged) {
      return;
    }

    result ??= lines.slice();
    result[index] = {
      ...line,
      originalTitle: line.title,
      ...(hasOverride && {title}),
      ...(legendChanged && {
        fancyTitle: {...line.fancyTitle, legend} as Line['fancyTitle'],
      }),
    };
  });

  return result ?? lines;
}

export function overrideBarTitles(
  bars: Bar[],
  overrides: {[key: string]: string},
  useRunOrGroupId: boolean
): Bar[] {
  return produce(bars, draft => {
    draft.forEach(l => {
      const lineKey = getLegendOverrideKeyBackwardsCompatible(
        l,
        useRunOrGroupId,
        overrides
      );
      l.originalTitle = l.title;
      if (lineKey in overrides) {
        l.title = overrides[lineKey];
        l.key = legendTemplateRemoveCrosshairValues(l.title);
      }
    });
  });
}

export function overrideBarColors(
  lines: Bar[],
  overrides: {[key: string]: {color: string; transparentColor: string}},
  useRunOrGroupId: boolean
): Bar[] {
  return produce(lines, draft => {
    draft.forEach(l => {
      const lineKey = getLegendOverrideKeyBackwardsCompatible(
        l,
        useRunOrGroupId,
        overrides
      );

      if (lineKey in overrides) {
        l.originalColor = l.color;
        l.color = overrides[lineKey].color;
      }
    });
  });
}

export function overrideLineColors(
  lines: Line[],
  overrides: {[key: string]: {color: string; transparentColor: string}},
  useRunOrGroupId: boolean
): Line[] {
  let result: Line[] | undefined;

  lines.forEach((line, index) => {
    const lineKey = getLegendOverrideKeyBackwardsCompatible(
      line,
      useRunOrGroupId,
      overrides
    );
    if (!(lineKey in overrides)) {
      return;
    }

    const color = line.aux
      ? overrides[lineKey].transparentColor
      : overrides[lineKey].color;
    const originalColorChanged =
      !Object.hasOwn(line, 'originalColor') ||
      line.originalColor !== line.color;
    const colorChanged = line.color !== color;

    if (!originalColorChanged && !colorChanged) {
      return;
    }

    result ??= lines.slice();
    result[index] = {
      ...line,
      color,
      originalColor: line.color,
    };
  });

  return result ?? lines;
}

export function overrideMarks(
  lines: Line[],
  overrides: {[key: string]: Mark},
  useRunOrGroupId: boolean
): Line[] {
  let result: Line[] | undefined;

  lines.forEach((line, index) => {
    const lineKey = getLegendOverrideKeyBackwardsCompatible(
      line,
      useRunOrGroupId,
      overrides
    );
    const hasOverride = lineKey in overrides;
    const mark = hasOverride ? overrides[lineKey] : line.mark;
    const originalMarkChanged =
      !Object.hasOwn(line, 'originalMark') || line.originalMark !== line.mark;
    const markChanged = hasOverride && line.mark !== mark;

    if (!originalMarkChanged && !markChanged) {
      return;
    }

    result ??= lines.slice();
    result[index] = {
      ...line,
      originalMark: line.mark,
      ...(hasOverride && {mark}),
    };
  });

  return result ?? lines;
}

export function overrideLineWidths(
  lines: Line[],
  overrides: {[key: string]: number},
  useRunOrGroupId: boolean
): Line[] {
  let result: Line[] | undefined;

  lines.forEach((line, index) => {
    const lineKey = getLegendOverrideKeyBackwardsCompatible(
      line,
      useRunOrGroupId,
      overrides
    );
    const hasOverride = lineKey in overrides;
    const lineWidth = hasOverride ? overrides[lineKey] : line.lineWidth;
    const originalLineWidthChanged =
      !Object.hasOwn(line, 'originalLineWidth') ||
      !Object.is(line.originalLineWidth, line.lineWidth);
    const lineWidthChanged = !Object.is(line.lineWidth, lineWidth);

    if (!originalLineWidthChanged && !lineWidthChanged) {
      return;
    }

    result ??= lines.slice();
    result[index] = {
      ...line,
      originalLineWidth: line.lineWidth,
      ...(hasOverride && {lineWidth}),
    };
  });

  return result ?? lines;
}
